import { Routes, Route, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Privacy from "./Privacy";

/* ================= HOME ================= */
function Home() {
  const [open, setOpen] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  useEffect(() => {
    // Thêm keyframes animation vào document
    if (!document.getElementById("overlay-animation")) {
      const style = document.createElement("style");
      style.id = "overlay-animation";
      style.textContent = `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }

    // Đảm bảo viewport cho mobile
    let viewport = document.querySelector("meta[name=viewport]");
    if (!viewport) {
      viewport = document.createElement("meta");
      viewport.name = "viewport";
      document.head.appendChild(viewport);
    }
    viewport.content = "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no";
  }, []);

  // Delay overlay sau khi menu slide xong (300ms)
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setShowOverlay(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowOverlay(false);
    }
  }, [open]);

  return (
    <div style={styles.page}>
      {/* OVERLAY - 40% bên phải để đóng menu */}
      {showOverlay && (
        <div 
          style={styles.overlay} 
          onClick={() => setOpen(false)}
        />
      )}

      {/* SIDE MENU - 60% width, slide từ trái */}
      <div style={{
        ...styles.sideMenu,
        transform: open ? "translateX(0)" : "translateX(-100%)"
      }}>
        <div style={styles.menuContent}>
          <h2 style={styles.menuTitle}>Menu</h2>
          <Link 
            to="/privacy" 
            style={styles.menuLink}
            onClick={() => setOpen(false)}
          >
            Privacy Policy
          </Link>
        </div>
      </div>

      {/* CONTENT */}
      <div style={styles.content}>
        {/* HEADER */}
        <div style={styles.header}>
          <button onClick={() => setOpen(!open)} style={styles.menuBtn}>☰</button>
          <h1 style={styles.logo}>NIMBOSK</h1>
          <div style={{width: 44}}></div> {/* Spacer để logo ở giữa */}
        </div>

        {/* DESCRIPTION */}
        <div style={styles.descWrap}>
          <Section text="NimBosk is a creative drawing and frame-by-frame animation app built for anyone who wants to bring ideas to life through motion. Designed with simplicity at its core, the app makes traditional animation techniques feel intuitive and accessible." />
          <Section text="Instead of overwhelming you with complex tools, NimBosk focuses on a smooth, distraction-free creative experience where drawing, editing, and animating flow naturally together. The interface stays clean and minimal so your attention remains on your art — not menus." />
          <Section text="With a lightweight and performance-optimized design, the app runs smoothly even during longer animation projects. You can create frame-by-frame animations, animated loops, experiment with timing, and explore motion freely." />
          <Section text="NimBosk was built with passion by a Vietnamese creator who believes powerful creative tools don't have to be complicated. Every feature follows real artistic workflow: quick sketching, easy frame control, and a natural drawing feel." />
          <Section text="Whether you're animating for social media, designing concepts, practicing motion studies, or relaxing with digital drawing, NimBosk adapts to your pace. It's a space to experiment, learn, and express ideas visually without pressure." />
          <hr style={styles.hrBold} />
          <p style={styles.tagline}>Creativity without barriers — just you, your ideas, and the freedom to turn still drawings into living motion.</p>
        </div>
      </div>

      {/* FOOTER */}
      <div style={styles.footer}>
        <p style={styles.support}>Support: nimbosk@gmail.com</p>

        <div style={{ marginTop: 16 }}>
          <p style={{margin: "8px 0"}}><Link to="/privacy" style={styles.footerLink}>Privacy Policy</Link></p>
        </div>

        <hr style={styles.hrFooter} />
        <p style={styles.copy}>© {new Date().getFullYear()} SketchinK. All rights reserved.</p>
      </div>
    </div>
  );
}

/* SECTION */
function Section({ text }) {
  return (
    <>
      <p style={{margin: "0 0 18px 0"}}>{text}</p>
      <hr style={styles.hr} />
    </>
  );
}

/* STYLES */
const styles = {
  page: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    margin: 0,
    padding: 0,
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    position: "relative"
  },

  // Overlay - 40% bên phải với animation fade-in
  overlay: {
    position: "fixed",
    top: 0,
    left: "60%",
    width: "40%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    zIndex: 998,
    cursor: "pointer",
    animation: "fadeIn 0.3s ease-in-out"
  },

  // Side Menu - 60% width
  sideMenu: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "60%",
    height: "100%",
    backgroundColor: "#fff",
    boxShadow: "2px 0 8px rgba(0,0,0,0.2)",
    zIndex: 999,
    transition: "transform 0.3s ease-in-out",
    overflowY: "auto"
  },

  menuContent: {
    padding: "24px 20px"
  },

  menuTitle: {
    fontSize: "clamp(22px, 5vw, 26px)",
    margin: "0 0 32px 0",
    fontWeight: "bold",
    color: "#000"
  },

  menuLink: {
    display: "block",
    fontSize: "clamp(16px, 4vw, 18px)",
    color: "#000",
    textDecoration: "none",
    padding: "16px 0",
    borderBottom: "1px solid #eee",
    WebkitTapHighlightColor: "transparent"
  },

  content: { 
    flex: 1,
    padding: "0 16px",
    width: "100%",
    boxSizing: "border-box"
  },

  header: { 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center",
    padding: "12px 0",
    position: "relative",
    minHeight: 60
  },

  menuBtn: {
    fontSize: 28,
    width: 44,
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    border: "none",
    fontWeight: "300",
    color: "#000",
    cursor: "pointer",
    padding: 0,
    margin: 0,
    lineHeight: 1,
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation"
  },

  logo: { 
    margin: 0,
    fontSize: "clamp(24px, 6vw, 32px)",
    color: "#000000",
    WebkitTextStroke: "3px #efefef",
    paintOrder: "stroke fill",
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    whiteSpace: "nowrap"
  },

  descWrap: { 
    fontSize: "clamp(16px, 4vw, 18px)",
    lineHeight: 1.7,
    marginTop: 20,
    paddingBottom: 40
  },

  hr: { 
    border: "none",
    borderTop: "1px solid #ddd",
    margin: "0 0 18px 0"
  },

  hrBold: { 
    border: "none",
    borderTop: "2px solid #aaa",
    margin: "24px 0"
  },

  tagline: { 
    fontWeight: "bold",
    fontSize: "clamp(18px, 4.5vw, 20px)",
    margin: "24px 0 0 0"
  },

  footer: {
    background: "#000",
    color: "#eee",
    padding: "32px 16px",
    marginTop: "auto",
    textAlign: "center"
  },

  support: { 
    margin: "0 0 6px 0",
    color: "#bbb",
    fontSize: "clamp(14px, 3.5vw, 16px)",
    wordBreak: "break-word"
  },

  footerLink: { 
    color: "#fff",
    textDecoration: "none",
    WebkitTapHighlightColor: "transparent"
  },

  hrFooter: { 
    border: "none",
    borderTop: "1px solid #333",
    margin: "20px auto",
    maxWidth: 400
  },

  copy: { 
    fontSize: "clamp(12px, 3vw, 13px)",
    color: "#777",
    margin: "16px 0 0 0"
  }
};

/* APP ROUTES */
export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/privacy" element={<Privacy />} />
    </Routes>
  );
}